/**
 * Creating a business. Also the first screen after signing up, because nothing in this app works
 * without one. Only the name is required: the tax details can wait until the first invoice needs
 * them, and the § 11 checklist will ask for them by itself.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateWorkspace } from "@/hooks/use-workspaces";
import { WORKSPACE_DEFAULTS } from "@/lib/api/workspaces";
import { isValidAustrianVatId, normalizeVatId } from "@/lib/at/uid";
import { useAuth } from "@/lib/session/auth-context";
import { applyServerErrors } from "@/lib/utils/form-errors";
import { track } from "@/lib/platform/analytics";
import { useWorkspace } from "@/lib/session/workspace-context";

/** Kept as a literal tuple so t(`legalForms.${value}`) is checked against en.json. */
const LEGAL_FORMS = [
  "sole_proprietor",
  "partnership",
  "gmbh",
  "flexco",
  "ag",
  "association",
  "other",
] as const;

export function WorkspaceNewPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { select, workspaces } = useWorkspace();
  const navigate = useNavigate();
  const createWorkspace = useCreateWorkspace(user?.id ?? "");

  const schema = z.object({
    name: z.string().min(2, t("validation.workspaceName")).max(120),
    legalForm: z.enum(LEGAL_FORMS),
    vatId: z
      .string()
      .refine((value) => value === "" || isValidAustrianVatId(value) || !/^AT/i.test(value), {
        message: t("validation.vatId"),
      }),
    accountingBasis: z.enum(["cash", "accrual"]),
    vatScheme: z.enum(["standard", "small_business"]),
    vatPeriod: z.enum(["monthly", "quarterly", "none"]),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      legalForm: "sole_proprietor",
      vatId: "",
      accountingBasis: "cash",
      vatScheme: "standard",
      vatPeriod: "quarterly",
    },
  });

  // A Kleinunternehmer files no UVA, so the period question disappears.
  const vatScheme = form.watch("vatScheme");

  async function onSubmit(values: Values) {
    try {
      const workspace = await createWorkspace.mutateAsync({
        ...WORKSPACE_DEFAULTS,
        ...values,
        vatId: normalizeVatId(values.vatId),
        legalName: values.name,
        vatPeriod: values.vatScheme === "small_business" ? "none" : values.vatPeriod,
      });
      select(workspace.id);
      track("workspace_created", { legalForm: values.legalForm });
      toast.success(t("workspace.created"));
      navigate("/dashboard");
    } catch (error) {
      const message = applyServerErrors(error, (name, err) =>
        form.setError(name as keyof Values, err),
      );
      if (message) toast.error(message);
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <Card>
        <CardHeader>
          <CardTitle>
            {workspaces.length === 0 ? t("workspace.firstTitle") : t("workspace.create")}
          </CardTitle>
          <CardDescription>
            {workspaces.length === 0 ? t("workspace.firstSubtitle") : t("workspace.createSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              className="space-y-5"
              data-testid="workspace-form"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("workspace.name")}</FormLabel>
                    <FormControl>
                      <Input autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="legalForm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("workspace.legalForm")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {LEGAL_FORMS.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`legalForms.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vatId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("workspace.vatId")}</FormLabel>
                    <FormControl>
                      <Input placeholder="ATU12345675" className="num" {...field} />
                    </FormControl>
                    <FormDescription>{t("workspace.vatIdHint")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="accountingBasis"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("workspace.accountingBasis")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cash">{t("accountingBasis.cash")}</SelectItem>
                          <SelectItem value="accrual">{t("accountingBasis.accrual")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vatScheme"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("workspace.vatScheme")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="vat-scheme">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="standard">{t("vatScheme.standard")}</SelectItem>
                          <SelectItem value="small_business">
                            {t("vatScheme.small_business")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {vatScheme === "standard" && (
                <FormField
                  control={form.control}
                  name="vatPeriod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("workspace.vatPeriod")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="monthly">{t("vatPeriod.monthly")}</SelectItem>
                          <SelectItem value="quarterly">{t("vatPeriod.quarterly")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>{t("workspace.vatPeriodHint")}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <Button type="submit" className="w-full" disabled={createWorkspace.isPending}>
                {t("workspace.submit")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
